import { useEffect, useRef, type KeyboardEvent } from 'react';
import { ArrowRight } from 'lucide-react';
import { JobIdentity } from './JobIdentity';
import { WorkspaceDialog } from './WorkspaceDialog';
import './app-link-job-chooser.css';

/**
 * "Which job?": a link to a place in a job (capturepoint://job/lookups and the rest,
 * docs/product/app-links.md) when the person has more than one job of their own. Each job
 * is its mark and its name, as on the job door; the one used last is first and has the
 * focus, so Enter opens it. One click opens that job at the place; closing it leaves
 * Paige where it was.
 */
export function AppLinkJobChooser({
  jobs,
  place,
  onChoose,
  onClose,
}: {
  jobs: readonly { id: string; name: string }[];
  /** The place by the app's own names, such as "Lookup lists". */
  place: string;
  onChoose: (jobId: string) => void;
  onClose: () => void;
}) {
  const list = useRef<HTMLUListElement>(null);
  const buttons = () => [...(list.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
  useEffect(() => {
    // After the window takes the focus for itself.
    const frame = window.requestAnimationFrame(() => buttons()[0]?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, []);
  const move = (event: KeyboardEvent<HTMLUListElement>) => {
    const all = buttons();
    const index = all.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'ArrowDown'
        ? index + 1
        : event.key === 'ArrowUp'
          ? index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? all.length - 1
              : null;
    if (next === null || !all.length) return;
    event.preventDefault();
    all[(next + all.length) % all.length]?.focus();
  };
  return (
    <WorkspaceDialog
      telemetry="link-job"
      title="Which job?"
      description={`Opens ${place}`}
      compact
      onClose={onClose}
    >
      {/* Choosing a job is the window's main button (telemetry counts the window done, not left). */}
      <form onSubmit={(event) => event.preventDefault()}>
        <ul ref={list} className="link-job-list" aria-label="Scan jobs" onKeyDown={move}>
          {jobs.map((job) => (
            <li key={job.id}>
              <button
                type="submit"
                data-telemetry="link-job-choose"
                onClick={() => onChoose(job.id)}
              >
                <JobIdentity jobId={job.id} />
                <span>{job.name}</span>
                <ArrowRight className="link-job-go" size={16} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </form>
    </WorkspaceDialog>
  );
}
